// src/context/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, getAuthForUserCreation } from '../services/firebase';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
} from 'firebase/auth';
import { doc, setDoc, getDocs, getDoc, collection } from 'firebase/firestore';
import { commitListRecipientsFirestoreTransactional } from '../contactManager/commitListRecipientsFirestore';
import { fetchServerRecipientsRowsForList } from '../contactManager/listContactsService';
import { userHasAdminRole, userHasProductionRole, setDesktopOptIn } from '../services/adminDesktopNotificationService';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    // Strip desktop notification opt-in for sessions that are neither Admin nor Production (shared devices / stale localStorage).
    useEffect(() => {
        if (!currentUser?.uid) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', currentUser.uid));
                if (cancelled) return;
                const data = snap.exists() ? snap.data() : {};
                if (!userHasAdminRole(data) && !userHasProductionRole(data)) {
                    setDesktopOptIn(false);
                }
            } catch {
                // Do not clear on transient errors — show() still gates on role; avoids wiping admin opt-in offline.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentUser?.uid]);

    const register = async (userDetails) => {
        const {
            email,
            password,
            username,
            companyName,
            locationNumber,
            fullName,
            contactInfo,
            billingAddress,
            shippingAddress,
            packingSlipNote,
            windowStart,
            windowEnd,
            poSystem,
            role,
            Pricing,
            active,
            initialPasswordReminderPending,
            initialPasswordChangeRequired,
            customXeroId,
            xeroContactId,
            createWithoutSigningIn,
        } = userDetails;

        const preserveCallerSession = createWithoutSigningIn === true;
        const authForCreate = getAuthForUserCreation({ preserveCallerSession });

        try {
            const userCredential = await createUserWithEmailAndPassword(authForCreate, email, password);
            const user = userCredential.user;

            if (preserveCallerSession) {
                await signOut(authForCreate);
            }

            const userDoc = {
                email,
                username,
                companyName,
                ...(locationNumber ? { locationNumber: String(locationNumber) } : {}),
                fullName,
                contactInfo: contactInfo || '',
                phone: contactInfo || '',
                billingAddress: {
                    line1: billingAddress.line1,
                    line2: billingAddress.line2,
                    line3: billingAddress.line3,
                    city: billingAddress.city,
                    postCode: billingAddress.postCode,
                    companyName: companyName, // Add company name to billing address
                },
                shippingAddress: {
                    line1: shippingAddress.line1,
                    line2: shippingAddress.line2,
                    line3: shippingAddress.line3,
                    city: shippingAddress.city,
                    postCode: shippingAddress.postCode,
                    companyName: companyName, // Add company name to shipping address
                },
                packingSlipNote,
                windowStart,
                windowEnd,
                poSystem,
                role,
                Pricing,
                active: active !== false,
                createdAt: new Date().toISOString(),
            };
            if (initialPasswordReminderPending === true) {
                userDoc.initialPasswordReminderPending = true;
            }
            if (initialPasswordChangeRequired === true) {
                userDoc.initialPasswordChangeRequired = true;
                userDoc.initialPasswordReminderPending = true;
            }
            const trimmedCustomXeroId = String(customXeroId || '').trim();
            if (trimmedCustomXeroId) {
                userDoc.customXeroId = trimmedCustomXeroId;
            }
            const trimmedXeroContactId = String(xeroContactId || '').trim();
            if (trimmedXeroContactId) {
                userDoc.xeroContactId = trimmedXeroContactId;
            }

            // Save user information in Firestore
            await setDoc(doc(db, 'users', user.uid), userDoc);

            if (!preserveCallerSession) {
                setCurrentUser(user);
            }

            // Promote any existing contacts (in lists) that match this email to Account
            try {
                const listsSnap = await getDocs(collection(db, 'lists'));
                for (const d of listsSnap.docs) {
                    const listRef = doc(db, 'lists', d.id);
                    const fresh = await getDoc(listRef);
                    if (!fresh.exists()) continue;
                    const data = fresh.data() || {};
                    // eslint-disable-next-line no-await-in-loop
                    const recs = await fetchServerRecipientsRowsForList(d.id, data);
                    let changed = false;
                    const next = recs.map((r) => {
                        const recEmail = String(r.email || r.Email || r['Email Address'] || '').trim().toLowerCase();
                        if (recEmail && recEmail === String(email).trim().toLowerCase()) {
                            changed = true;
                            const chips = Array.from(new Set([...(r.chips || []), 'Has Account', 'Account: Active']));
                            const leadStatus = 'Won (Account)';
                            return { ...r, chips, leadStatus };
                        }
                        return r;
                    });
                    if (changed) {
                        await commitListRecipientsFirestoreTransactional(d.id, next, {
                            snapshotSource: 'auth_register',
                            snapshotActorUid: user.uid,
                            snapshotActorEmail: email,
                        });
                    }
                }
            } catch (e) {
                // Non-fatal: logging for visibility but do not block registration flow
                console.warn('[Auth] Failed to promote contact to account in lists:', e);
            }
            return { uid: user.uid, email: user.email || email };
        } catch (error) {
            console.error('Error during registration:', error);
            throw error;
        }
    };

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const logout = async () => {
        try {
            const u = auth.currentUser;
            if (u) {
                const snap = await getDoc(doc(db, 'users', u.uid));
                const data = snap.exists() ? snap.data() : {};
                if (!userHasAdminRole(data) && !userHasProductionRole(data)) {
                    setDesktopOptIn(false);
                }
            }
        } catch {
            // Cannot verify role — clear opt-in so the next person on a shared machine is not treated as opted-in.
            setDesktopOptIn(false);
        }
        return signOut(auth);
    };

    const value = {
        user: currentUser,
        login,
        logout,
        register,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
